import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
} from "@mui/material";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import InsightsIcon from "@mui/icons-material/Insights";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import NotesCarousel from "../../components/NotesCarousel/NotesCarousel";

import axios from "axios";
import API_BASE_URL from "../../utils/api";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

const GOLD = "#FFD700";
const QUARTER_RANGES = {
  1: "Jan 1 - Mar 31",
  2: "Apr 1 - Jun 30",
  3: "Jul 1 - Sep 30",
  4: "Oct 1 - Dec 31",
};

function describeQuarter(quarterKey) {
  if (!quarterKey) return "";
  const [yearStr, qStr] = quarterKey.split("-Q");
  const year = Number(yearStr);
  const q = Number(qStr);
  if (!year || !q || !QUARTER_RANGES[q]) return "";
  return `${QUARTER_RANGES[q]} ${year}`;
}

const GranteeCard = ({ g, idx }) => {
  const isActive = g.hasUser;
  const hasReceipts = isActive && g.files && g.files.length > 0;

  const daily = Array.isArray(g.daily) ? g.daily : [];
  const quarterSeries = Array.isArray(g.series) ? g.series : [];

  const hasDaily = daily.length > 0;
  const quarterKeys = quarterSeries.map((q) => q.quarterKey);

  const [selectedQuarter, setSelectedQuarter] = useState("ALL");

  const filteredDaily = !hasDaily
    ? []
    : selectedQuarter === "ALL"
      ? daily
      : daily.filter((d) => d.quarterKey === selectedQuarter);

  const totalDocs = g.totalDigitized || 0;

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        border: `1px solid ${GOLD}33`,
        display: "flex",
        flexDirection: "column",
        minHeight: 260,
        bgcolor: isActive ? "#ffffff" : "#f5f5f5",
        opacity: isActive ? 1 : 0.65,
      }}
    >
      <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            mb: 1.5,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {g.name && g.hasUser ? g.name : "Not yet registered"}
              <WorkspacePremiumIcon
                fontSize="small"
                sx={{ color: GOLD }}
                titleAccess="Grantee"
              />
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {g.email}
            </Typography>
            {g.note && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Note: {g.note}
              </Typography>
            )}
          </Box>
          <Chip
            size="small"
            label={
              !isActive
                ? "Not signed up"
                : `${totalDocs} doc${totalDocs === 1 ? "" : "s"}`
            }
            sx={{
              bgcolor: !isActive ? "#e0e0e0" : "#fff9d6",
              borderColor: isActive ? GOLD : "#bdbdbd",
              borderWidth: 1,
              borderStyle: "solid",
              fontWeight: 600,
            }}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 1,
            }}
          >
            <InsightsIcon fontSize="small" sx={{ color: GOLD }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Digitized documents over time
            </Typography>
          </Box>
          {!isActive ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontStyle: "italic" }}
            >
              User hasn&apos;t signed up yet. No progress data.
            </Typography>
          ) : hasDaily ? (
            <>
              <Box
                sx={{
                  mb: 1,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                }}
              >
                <Chip
                  size="small"
                  label="Overview – all quarters"
                  onClick={() => setSelectedQuarter("ALL")}
                  sx={{
                    borderRadius: 999,
                    bgcolor: selectedQuarter === "ALL" ? "#fff9d6" : "#f5f5f5",
                    border: `1px solid ${
                      selectedQuarter === "ALL" ? GOLD : "#ddd"
                    }`,
                    fontWeight: selectedQuarter === "ALL" ? 600 : 400,
                  }}
                />
                {quarterSeries.map((q) => (
                  <Chip
                    key={q.quarterKey}
                    size="small"
                    label={`${q.quarterKey} – ${
                      q.digitizedCount || 0
                    } doc${q.digitizedCount === 1 ? "" : "s"}`}
                    onClick={() => setSelectedQuarter(q.quarterKey)}
                    sx={{
                      borderRadius: 999,
                      bgcolor:
                        selectedQuarter === q.quarterKey
                          ? "#fff9d6"
                          : "#f5f5f5",
                      border: `1px solid ${
                        selectedQuarter === q.quarterKey ? GOLD : "#ddd"
                      }`,
                      fontWeight: selectedQuarter === q.quarterKey ? 600 : 400,
                    }}
                  />
                ))}
              </Box>
              {selectedQuarter !== "ALL" && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  {selectedQuarter} covers {describeQuarter(selectedQuarter)}.
                </Typography>
              )}
              {filteredDaily.length > 0 ? (
                <Box sx={{ height: 160 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={filteredDaily}
                      margin={{ top: 10, right: 8, left: 0, bottom: 10 }}
                    >
                      <defs>
                        <linearGradient
                          id={`colorDocs-${g.userId || idx}`}
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={GOLD}
                            stopOpacity={0.9}
                          />
                          <stop
                            offset="95%"
                            stopColor={GOLD}
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        padding={{ left: 20, right: 20 }}
                        tickFormatter={(value) => {
                          const d = new Date(value);
                          if (Number.isNaN(d.getTime())) return value;
                          return d.toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                          });
                        }}
                        label={{
                          value: "Date",
                          position: "insideBottom",
                          offset: -5,
                        }}
                      />
                      <YAxis
                        allowDecimals={false}
                        label={{
                          value: "Docs",
                          angle: -90,
                          position: "insideLeft",
                        }}
                      />
                      <RechartsTooltip
                        labelFormatter={(value) => {
                          const d = new Date(value);
                          if (Number.isNaN(d.getTime())) return value;
                          return d.toLocaleDateString();
                        }}
                        formatter={(val, _name, ctx) => {
                          const point = ctx?.payload;
                          const baseLabel = "Cumulative total";
                          if (
                            point?.deltaSum != null &&
                            point?.updates != null
                          ) {
                            const extra = ` (added ${point.deltaSum} doc${
                              point.deltaSum === 1 ? "" : "s"
                            } in ${point.updates} update${
                              point.updates === 1 ? "" : "s"
                            })`;
                            return [`${val}${extra}`, baseLabel];
                          }
                          return [val, baseLabel];
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="total"
                        stroke={GOLD}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill={`url(#colorDocs-${g.userId || idx})`}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Alert severity="info">
                  No activity recorded in {selectedQuarter}.
                </Alert>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                This chart shows the running total of documents over time. Use
                the chips above to view all quarters or a single quarter&apos;s
                activity.
              </Typography>
            </>
          ) : quarterSeries.length ? (
            <>
              <Box sx={{ height: 140 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={quarterSeries}
                    margin={{ top: 10, right: 8, left: 0, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient
                        id={`colorDocs-${g.userId || idx}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="5%" stopColor={GOLD} stopOpacity={0.9} />
                        <stop offset="95%" stopColor={GOLD} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" />

                    <XAxis
                      dataKey="quarterKey"
                      tick={{ fontSize: 10 }}
                      height={28}
                      label={{
                        value: "Quarter",
                        position: "insideBottom",
                        offset: -2,
                      }}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 10 }}
                      width={36}
                      label={{
                        value: "Docs",
                        angle: -90,
                        position: "insideLeft",
                        offset: 10,
                      }}
                    />
                    <RechartsTooltip />

                    <Area
                      type="monotone"
                      dataKey="digitizedCount"
                      stroke={GOLD}
                      strokeWidth={2}
                      fill={`url(#colorDocs-${g.userId || idx})`}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                No per-day logs for this user yet, so we&apos;re showing totals
                per quarter.
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No progress recorded yet. Total for all quarters:
              <b>{totalDocs}</b>.
            </Typography>
          )}
        </Box>
        {/* Notes */}
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}
          >
            <StickyNote2Icon fontSize="small" sx={{ color: GOLD }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Project Notes
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: "auto" }}
            >
              {isActive ? g.notes?.length || 0 : 0} note(s)
            </Typography>
          </Box>
          {!isActive ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 12 }}
            >
              No notes — user hasn&apos;t signed up yet.
            </Typography>
          ) : (
            <NotesCarousel
              notes={g.notes}
              max={10}
              emptyText="No notes posted yet."
            />
          )}
        </Box>
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 0.5,
            }}
          >
            <ReceiptLongIcon fontSize="small" sx={{ color: GOLD }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Receipts
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: "auto" }}
            >
              {isActive ? g.files?.length || 0 : 0} file(s)
            </Typography>
          </Box>
          {!isActive ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 12 }}
            >
              No receipts — user hasn&apos;t signed up yet.
            </Typography>
          ) : hasReceipts ? (
            <>
              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2,
                  maxHeight: 150,
                  overflowY: "auto",
                  pr: 0.5,
                  borderRadius: 1,
                  border: "1px solid #eee",
                  bgcolor: "#fafafa",
                }}
              >
                {g.files.map((f) => (
                  <li key={f.url} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      {f.purchaseType || "Receipt"}
                    </div>
                    <div style={{ fontSize: 12, color: "#555" }}>
                      Amount:
                      {f.amount ? `$${Number(f.amount).toFixed(2)}` : "-"}
                      {" · "} Date purchased:
                      {f.purchaseDate
                        ? new Date(f.purchaseDate).toLocaleDateString()
                        : "-"}
                      {" · "}
                      Uploaded:
                      {f.lastModified
                        ? new Date(f.lastModified).toLocaleDateString()
                        : "-"}
                    </div>
                    <div style={{ fontSize: 12 }}>
                      <a href={f.url} target="_blank" rel="noreferrer">
                        View file ({(f.size / 1024).toFixed(1)} KB)
                      </a>
                    </div>
                  </li>
                ))}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Scroll inside this box to see all receipt files.
              </Typography>
            </>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 12 }}
            >
              No receipts uploaded yet.
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
const AdminGrantsOverview = () => {
  useEffect(() => {
    document.title = "Manage Grants - MS Digital Hub";
  }, []);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [grantees, setGrantees] = useState([]); // one item per allowlist entry

  useEffect(() => {
    const fetchOverview = async () => {
      setLoading(true);
      setErr("");
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API_BASE_URL}/grants/admin/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGrantees(res.data.data || []);
      } catch (e) {
        setErr(e.response?.data?.error || "Failed to load grants overview.");
      } finally {
        setLoading(false);
      }
    };

    fetchOverview();
  }, []);

  return (
    <Box
      sx={{
        width: "auto",

        padding: "0px 20px",
        margin: "0px auto",
        padding: { xs: "20px", sm: "30px", md: "1px 80px" },
        border: "none",
        backgroundColor: "#ffffff",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          mb: 3,
          pb: 2,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <WorkspacePremiumIcon sx={{ color: GOLD }} />
            <Typography variant="h4">Grants Overview</Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            Each card represents one grantee from the allowlist: sign-up status,
            per-quarter digitized totals, and uploaded receipts.
          </Typography>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {err && (
        <Alert sx={{ mb: 2 }} severity="error">
          {err}
        </Alert>
      )}
      {!loading && !err && grantees.length === 0 && (
        <Alert severity="info">No grantees in the allowlist yet.</Alert>
      )}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr",
            lg: "1fr 1fr 1fr",
          },
          gap: 3,
        }}
      >
        {grantees.map((g, idx) => (
          <GranteeCard key={`${g.email}-${idx}`} g={g} idx={idx} />
        ))}
      </Box>
    </Box>
  );
};

export default AdminGrantsOverview;
